import { useEffect, useRef, useState } from "react";
import { clamp } from "@/utilities/clamp";

type Coord = [number, number, string, boolean];

type UseAnimatedPositionsOptions = {
  fallbackDurationMs?: number;
  minDurationMs?: number;
  maxDurationMs?: number;
  // km/h from status API. Optional and read as a hint for smoothing duration.
  speedByDeviceId?: Record<string, number | undefined>;
};

type TargetMeta = {
  lng: number;
  lat: number;
  updatedAtMs: number;
};

const EARTH_RADIUS_METERS = 6371000;
const POSITION_CHANGE_EPSILON_METERS = 1.5;
const MIN_VALID_SPEED_KMH = 1;
const CADENCE_SMOOTHING_FACTOR = 0.9;

function toCoordMap(coords: Coord[]): Map<string, Coord> {
  return new Map(coords.map(([lng, lat, id, driving]) => [id, [lng, lat, id, driving] as Coord]));
}

function haversineMeters(fromLng: number, fromLat: number, toLng: number, toLat: number): number {
  const dLat = ((toLat - fromLat) * Math.PI) / 180;
  const dLng = ((toLng - fromLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((fromLat * Math.PI) / 180) *
      Math.cos((toLat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

export function useAnimatedPositions(latest: Coord[], options?: UseAnimatedPositionsOptions) {
  const fallbackDurationMs = options?.fallbackDurationMs ?? 4000;
  const minDurationMs = options?.minDurationMs ?? 1000;
  const maxDurationMs = options?.maxDurationMs ?? 45000;
  const [animatedCoords, setAnimatedCoords] = useState<Coord[]>(latest);
  const currentCoordsRef = useRef<Map<string, Coord>>(toCoordMap(latest));
  const lastTargetMetaRef = useRef<Map<string, TargetMeta>>(new Map());
  const speedByDeviceIdRef = useRef<Record<string, number | undefined>>(
    options?.speedByDeviceId ?? {},
  );

  // Keep speed hints up to date without restarting interpolation on every status poll.
  useEffect(() => {
    speedByDeviceIdRef.current = options?.speedByDeviceId ?? {};
  }, [options?.speedByDeviceId]);

  useEffect(() => {
    if (fallbackDurationMs <= 0 || latest.length === 0) {
      setAnimatedCoords(latest);
      currentCoordsRef.current = toCoordMap(latest);
      if (latest.length === 0) {
        lastTargetMetaRef.current.clear();
      }
      return;
    }

    const startMs = performance.now();
    const fromMap = currentCoordsRef.current;
    const toMap = toCoordMap(latest);
    const previousMeta = lastTargetMetaRef.current;
    const nextMeta = new globalThis.Map<string, TargetMeta>();
    const durationById = new globalThis.Map<string, number>();

    for (const [id, to] of toMap.entries()) {
      const from = fromMap.get(id) ?? to;
      const movementMeters = haversineMeters(from[0], from[1], to[0], to[1]);
      const meta = previousMeta.get(id);
      const targetChanged =
        !meta || haversineMeters(meta.lng, meta.lat, to[0], to[1]) > POSITION_CHANGE_EPSILON_METERS;

      if (movementMeters <= POSITION_CHANGE_EPSILON_METERS) {
        durationById.set(id, 0);
      } else {
        const cadenceMs = meta && targetChanged ? startMs - meta.updatedAtMs : fallbackDurationMs;
        const cadenceDurationMs = Math.max(
          fallbackDurationMs,
          cadenceMs * CADENCE_SMOOTHING_FACTOR,
        );
        const speedKmh = speedByDeviceIdRef.current[id];
        const speedDurationMs =
          speedKmh != null && Number.isFinite(speedKmh) && speedKmh > MIN_VALID_SPEED_KMH
            ? (movementMeters / ((speedKmh * 1000) / 3600)) * 1000
            : 0;
        const durationMs = clamp(
          Math.max(cadenceDurationMs, speedDurationMs),
          minDurationMs,
          maxDurationMs,
        );
        durationById.set(id, durationMs);
      }

      if (targetChanged || !meta) {
        nextMeta.set(id, { lng: to[0], lat: to[1], updatedAtMs: startMs });
      } else {
        nextMeta.set(id, meta);
      }
    }
    lastTargetMetaRef.current = nextMeta;

    let rafId: number;

    function animate(timeMs: number) {
      const nextFrame: Coord[] = [];
      const nextFrameMap = new globalThis.Map<string, Coord>();
      let hasActiveAnimation = false;

      for (const [id, to] of toMap.entries()) {
        const from = fromMap.get(id) ?? to;
        const durationMs = durationById.get(id) ?? 0;

        if (durationMs <= 0) {
          nextFrame.push(to);
          nextFrameMap.set(id, to);
          continue;
        }

        const t = Math.min(1, (timeMs - startMs) / durationMs);
        const easedT = smoothstep(t);
        const lng = from[0] + (to[0] - from[0]) * easedT;
        const lat = from[1] + (to[1] - from[1]) * easedT;
        const next: Coord = [lng, lat, id, to[3]];
        nextFrame.push(next);
        nextFrameMap.set(id, next);
        if (t < 1) hasActiveAnimation = true;
      }

      // Keep the current animated frame in a ref so new polling cycles can
      // continue smoothly from wherever the previous animation was interrupted.
      currentCoordsRef.current = nextFrameMap;
      setAnimatedCoords(nextFrame);

      if (hasActiveAnimation) rafId = requestAnimationFrame(animate);
      else currentCoordsRef.current = toMap;
    }

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [latest, fallbackDurationMs, minDurationMs, maxDurationMs]);

  return animatedCoords;
}
